// หน้าทดสอบ API e-Contract: the tester types in a made-up Sale Order, then uses the
// normal rental form. The contract is sent to e-Contract (UAT) for real.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ThaiAddressSearch from './ThaiAddressSearch';

const GROUPS = [
  { value: 'HK-R01', label: 'HK-R01 · เครื่องขัด/ล้างพื้น → แบบสัญญาที่ 2' },
  { value: 'KT-R01', label: 'KT-R01 · เครื่องครัว → แบบสัญญาที่ 1' },
  { value: 'LD-R01', label: 'LD-R01 · กลุ่มอื่น (ทดสอบกรณีเลือกแบบสัญญาเอง)' }
];

const blankItem = (over = {}) => ({ productName: '', groupName: 'HK-R01', qty: '1', price: '', ...over });

const SAMPLE = {
  customerName: 'บริษัท ทดสอบระบบ จำกัด',
  subject: 'ทดสอบสัญญาเช่าเครื่อง',
  address1: '99 ถนนทดสอบ',
  address2: '',
  address: { subdistrict: 'สามเสนนอก', district: 'ห้วยขวาง', province: 'กรุงเทพมหานคร', postcode: '10310' },
  items: [blankItem({ productName: 'เครื่องขัดพื้น (ตัวอย่าง)', groupName: 'HK-R01', qty: '1', price: '4000' })]
};

const STATUS_LABEL = { awaiting: 'รอรับงาน', pending: 'รอดำเนินการ', approved: 'อนุมัติ', cancelled: 'ยกเลิก', expired: 'หมดอายุ', terminated: 'ไม่อนุมัติ', draft: 'ฉบับร่าง', sent: 'ส่งแล้ว' };

const UatRental = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(SAMPLE);
  const [creating, setCreating] = useState(false);
  const [orders, setOrders] = useState([]);

  const loadOrders = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/uat/rental/orders`);
      setOrders(resp.data.orders || []);
    } catch (err) { /* list is a convenience */ }
  }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const setItem = (index, patch) => setForm(prev => ({ ...prev, items: prev.items.map((it, i) => (i === index ? { ...it, ...patch } : it)) }));
  const money = (v) => String(v).replace(/[^0-9.]/g, '');

  const create = async () => {
    setCreating(true);
    try {
      const resp = await axios.post(`${API_URL}/uat/rental/orders`, {
        customerName: form.customerName,
        subject: form.subject,
        address: { address1: form.address1, address2: form.address2, ...form.address },
        items: form.items
      });
      toast.success('สร้างออเดอร์ทดสอบแล้ว');
      navigate(`/uat/rental/${resp.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'สร้างออเดอร์ทดสอบไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/uat/rental/orders/${id}`);
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch (err) {
      toast.error('ลบไม่สำเร็จ');
    }
  };

  return (
    <div className="rs uat">
      <header className="rs-header">
        <div>
          <h1 className="rs-title">ทดสอบ API สัญญาเช่า (e-Contract)</h1>
          <p className="rs-subtitle">กรอกใบสั่งขายสมมติ แล้วทดสอบการส่ง แก้ไข ตรวจสถานะ ยกเลิก และต่ออายุสัญญา</p>
        </div>
      </header>

      <div className="uat-banner" role="note">
        <i className="bi bi-flask"></i>
        <div>
          <b>ข้อมูลที่กรอกเป็นข้อมูลสมมติ แต่การส่งสัญญาเป็นของจริง</b> สัญญาจะถูกส่งไปที่ระบบ e-Contract (UAT) ด้วย API เดียวกับที่ใช้งานจริง
          เลขที่สัญญาขึ้นต้นด้วย <b>TEST-DC</b> และออเดอร์ทดสอบจะไม่ปรากฏในรายการเอกสารเครื่องเช่าจริง
          ถ้าใส่อีเมลผู้อนุมัติ e-Contract อาจส่งอีเมลถึงบุคคลนั้นจริง ควรใช้อีเมลของตัวเอง
        </div>
      </div>

      <section className="rs-card">
        <div className="rs-card__head"><div className="rs-card__headtext">
          <div className="rs-card__kicker">ขั้นที่ 1</div><h2 className="rs-card__title">สร้างใบสั่งขายทดสอบ</h2>
          <p className="rs-card__desc">แก้ข้อมูลได้ตามต้องการ เช่น ชื่อลูกค้า ที่อยู่ และเครื่องที่เช่า</p>
        </div></div>

        <div className="rs-grid">
          <div className="rs-field rs-field--wide">
            <label htmlFor="uc">ชื่อลูกค้า <span className="rs-req">*</span></label>
            <input id="uc" className="rs-input" value={form.customerName} onChange={(e) => setField('customerName', e.target.value)} />
          </div>
          <div className="rs-field">
            <label htmlFor="us">เรื่อง / ชื่อใบสั่งขาย</label>
            <input id="us" className="rs-input" value={form.subject} onChange={(e) => setField('subject', e.target.value)} />
          </div>
          <div className="rs-field rs-field--full">
            <label htmlFor="ua1">ที่อยู่ลูกค้า</label>
            <input id="ua1" className="rs-input" value={form.address1} onChange={(e) => setField('address1', e.target.value)} placeholder="เลขที่ ถนน ซอย" />
          </div>
          <div className="rs-field rs-field--full">
            <label>ตำบล / อำเภอ / จังหวัด</label>
            <ThaiAddressSearch
              value={form.address}
              onSelect={(a) => setField('address', { subdistrict: a.subdistrict, district: a.district, province: a.province, postcode: a.zipcode || '' })}
            />
            <span className="rs-help">รหัสไปรษณีย์: {form.address.postcode || '—'}</span>
          </div>
        </div>

        <div className="rs-subhead">เครื่องที่เช่า</div>
        <p className="rs-help" style={{ margin: "0 0 10px" }}>กลุ่มสินค้าเป็นตัวกำหนดแบบสัญญา เช่น HK ใช้แบบที่ 2 และ KT ใช้แบบที่ 1</p>
        <div className="uat-items">
          <div className="uat-items__row uat-items__row--head" aria-hidden="true">
            <span>ชื่อเครื่อง</span><span>กลุ่มสินค้า</span><span>จำนวน</span><span>ราคา (บาท)</span><span></span>
          </div>
          {form.items.map((it, i) => (
            <div className="uat-items__row" key={i}>
              <input className="rs-input" aria-label={`ชื่อเครื่อง ${i + 1}`} placeholder="ชื่อเครื่อง" value={it.productName} onChange={(e) => setItem(i, { productName: e.target.value })} />
              <select className="rs-input" aria-label={`กลุ่มสินค้า ${i + 1}`} value={it.groupName} onChange={(e) => setItem(i, { groupName: e.target.value })}>
                {GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
              <input className="rs-input" aria-label={`จำนวน ${i + 1}`} inputMode="numeric" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value.replace(/\D/g, '') })} />
              <input className="rs-input" aria-label={`ราคา ${i + 1}`} inputMode="decimal" placeholder="ราคา" value={it.price} onChange={(e) => setItem(i, { price: money(e.target.value) })} />
              <button type="button" className="econ-icon-btn econ-icon-btn--danger" disabled={form.items.length < 2} onClick={() => setForm(prev => ({ ...prev, items: prev.items.filter((_, k) => k !== i) }))} title="ลบรายการ"><i className="bi bi-trash"></i></button>
            </div>
          ))}
        </div>
        <button type="button" className="econ-link-btn" onClick={() => setForm(prev => ({ ...prev, items: [...prev.items, blankItem({ groupName: 'KT-R01', productName: 'เตาอบ (ตัวอย่าง)', price: '9000' })] }))}>
          <i className="bi bi-plus-circle"></i> เพิ่มเครื่อง
        </button>

        <div style={{ marginTop: 18 }}>
          <button type="button" className="rs-btn rs-btn--primary" onClick={create} disabled={creating}>
            {creating ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-play-fill"></i>} สร้างใบสั่งขายทดสอบและเริ่มทดสอบ
          </button>
        </div>
      </section>

      <section className="rs-card">
        <div className="rs-card__head"><div className="rs-card__headtext">
          <div className="rs-card__kicker">ขั้นที่ 2</div><h2 className="rs-card__title">ทดสอบอะไรได้บ้าง</h2>
        </div></div>
        <div className="rs-table-wrap">
          <table className="rs-table">
            <thead><tr><th>ที่หน้าฟอร์ม</th><th>เรียก API</th></tr></thead>
            <tbody>
              <tr><td>ค้นหาหนังสือมอบอำนาจ (POA)</td><td>รายการ POA</td></tr>
              <tr><td>กด "ตรวจสอบและส่งสัญญา"</td><td>สร้างสัญญา · ส่งอีกครั้งเพื่อแก้ไข (ขณะ "รอรับงาน")</td></tr>
              <tr><td>กด "รีเฟรชสถานะ"</td><td>ดูสัญญา (สถานะ ยอดเงิน ความคืบหน้าการลงนาม)</td></tr>
              <tr><td>กด "ยกเลิกสัญญา"</td><td>ยกเลิกสัญญา — ใช้ได้เมื่อสัญญาอนุมัติแล้วเท่านั้น</td></tr>
              <tr><td>กด "ต่ออายุสัญญา"</td><td>ต่ออายุสัญญา — ใช้ได้เมื่อสัญญาอนุมัติแล้วเท่านั้น</td></tr>
            </tbody>
          </table>
        </div>
        <p className="rs-help">การยกเลิกและต่ออายุต้องมีสัญญาที่ "อนุมัติ" แล้ว ให้ลงนามสัญญาทดสอบในเว็บ e-Contract (UAT) ก่อน แล้วกลับมากดรีเฟรชสถานะ</p>
      </section>

      <section className="rs-card">
        <div className="rs-card__head"><div className="rs-card__headtext">
          <div className="rs-card__kicker">ของฉัน</div><h2 className="rs-card__title">ออเดอร์ทดสอบของฉัน</h2>
        </div></div>
        {orders.length === 0 ? (
          <p className="rs-help">ยังไม่มีออเดอร์ทดสอบ</p>
        ) : (
          <div className="rs-table-wrap">
            <table className="rs-table">
              <thead><tr><th>ออเดอร์</th><th>ลูกค้า</th><th>เลขที่สัญญา</th><th>สถานะ</th><th></th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td className="rs-mono">{o.soNumber}</td>
                    <td>{o.customerName}</td>
                    <td className="rs-mono">{o.contractNumber || '—'}</td>
                    <td>{o.contractStatus ? (STATUS_LABEL[o.contractStatus] || o.contractStatus) : 'ยังไม่ส่ง'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <Link className="econ-link-btn" to={`/uat/rental/${o.id}`}>เปิด</Link>{' · '}
                      <button type="button" className="econ-link-btn" onClick={() => remove(o.id)}>ลบ</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="rs-help">การลบจะลบเฉพาะออเดอร์ในระบบนี้ สัญญาที่สร้างบน e-Contract แล้วจะยังอยู่ที่นั่น</p>
      </section>
    </div>
  );
};

export default UatRental;
